import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  CreditCard as CreditCardIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import {
  WalteroCard,
  WalteroButton,
  WalteroStatusChip,
  WalteroTypography,
  WalteroSkeleton
} from './waltero';
import billingService from '../services/billingService';
import AddPaymentMethodModal from './AddPaymentMethodModal';

/**
 * Payment Methods Tab
 *
 * Displays and manages payment methods for an organization
 * Features:
 * - List all payment methods
 * - Add new payment method (Stripe Elements)
 * - Set default payment method
 * - Remove payment methods
 */
const PaymentMethodsTab = ({ organizationId }) => {
  const theme = useTheme();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [methodToDelete, setMethodToDelete] = useState(null);
  const [processing, setProcessing] = useState(false);

  // Load payment methods
  useEffect(() => {
    if (organizationId && organizationId !== '') {
      loadPaymentMethods();
    } else {
      setLoading(false);
      setPaymentMethods([]);
    }
  }, [organizationId]);

  const loadPaymentMethods = async () => {
    if (!organizationId || organizationId === '') {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await billingService.getPaymentMethods(organizationId);
      setPaymentMethods(data.paymentMethods || []);
    } catch (err) {
      console.error('Error loading payment methods:', err);
      setError(err.error || 'Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  };

  // Set payment method as default
  const handleSetDefault = async (paymentMethodId) => {
    try {
      setProcessing(true);
      await billingService.setDefaultPaymentMethod(organizationId, paymentMethodId);
      await loadPaymentMethods(); // Reload to get updated default status
    } catch (err) {
      console.error('Error setting default payment method:', err);
      setError(err.error || 'Failed to set default payment method');
    } finally {
      setProcessing(false);
    }
  };

  // Delete payment method
  const handleDeleteConfirm = async () => {
    if (!methodToDelete) return;

    try {
      setProcessing(true);
      await billingService.removePaymentMethod(organizationId, methodToDelete.id);
      setDeleteDialogOpen(false);
      setMethodToDelete(null);
      await loadPaymentMethods(); // Reload list
    } catch (err) {
      console.error('Error removing payment method:', err);
      setError(err.error || 'Failed to remove payment method');
    } finally {
      setProcessing(false);
    }
  };

  // Get card brand icon
  const getCardBrandIcon = (brand) => {
    const brandIcons = {
      visa: '💳 Visa',
      mastercard: '💳 Mastercard',
      amex: '💳 Amex',
      discover: '💳 Discover',
      diners: '💳 Diners',
      jcb: '💳 JCB',
      unionpay: '💳 UnionPay'
    };
    return brandIcons[brand?.toLowerCase()] || '💳 Card';
  };

  // Format expiry date
  const formatExpiry = (expMonth, expYear) => {
    return `${String(expMonth).padStart(2, '0')}/${expYear}`;
  };

  // Render loading state
  if (loading) {
    return (
      <Box>
        <Grid container spacing={2}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} md={6} lg={4} key={i}>
              <WalteroSkeleton variant="rectangular" height={180} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <WalteroTypography variant="h5" fontWeight="bold">
          Payment Methods
        </WalteroTypography>
        <WalteroButton
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddModalOpen(true)}
          disabled={processing}
        >
          ADD PAYMENT METHOD
        </WalteroButton>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Payment Methods Grid */}
      {paymentMethods.length === 0 ? (
        <WalteroCard sx={{ p: 4, textAlign: 'center' }}>
          <CreditCardIcon
            sx={{
              fontSize: 64,
              color: theme.palette.grey[400],
              mb: 2
            }}
          />
          <Typography variant="h6" gutterBottom>
            No Payment Methods
          </Typography>
          <Typography color="textSecondary" paragraph>
            Add a payment method to activate subscriptions and manage billing
          </Typography>
          <WalteroButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddModalOpen(true)}
          >
            ADD YOUR FIRST PAYMENT METHOD
          </WalteroButton>
        </WalteroCard>
      ) : (
        <Grid container spacing={2}>
          {paymentMethods.map((method) => (
            <Grid item xs={12} md={6} lg={4} key={method.id}>
              <WalteroCard
                sx={{
                  p: 3,
                  position: 'relative',
                  border: method.isDefault
                    ? `2px solid ${theme.palette.waltero.primary.coreBlue}`
                    : '1px solid transparent'
                }}
              >
                {/* Default Badge */}
                {method.isDefault && (
                  <Chip
                    label="DEFAULT"
                    size="small"
                    icon={<StarIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      backgroundColor: theme.palette.waltero.primary.coreBlue,
                      color: 'white'
                    }}
                  />
                )}

                {/* Card Details */}
                <Box display="flex" alignItems="center" mb={2}>
                  <CreditCardIcon
                    sx={{
                      fontSize: 32,
                      color: theme.palette.waltero.primary.coreBlue,
                      mr: 2
                    }}
                  />
                  <Box flex={1}>
                    <Typography variant="h6">
                      {getCardBrandIcon(method.brand)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      •••• {method.last4}
                    </Typography>
                  </Box>
                </Box>

                {/* Expiry Date */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="body2" color="textSecondary">
                    Expires
                  </Typography>
                  <Typography variant="body1">
                    {formatExpiry(method.expiryMonth, method.expiryYear)}
                  </Typography>
                </Box>

                {/* Actions */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  {!method.isDefault ? (
                    <WalteroButton
                      size="small"
                      startIcon={<StarBorderIcon />}
                      onClick={() => handleSetDefault(method.id)}
                      disabled={processing}
                    >
                      SET AS DEFAULT
                    </WalteroButton>
                  ) : (
                    <Box /> // Empty box for spacing
                  )}

                  <Tooltip title={method.isDefault ? "Cannot delete default payment method" : "Remove payment method"}>
                    <span>
                      <IconButton
                        color="error"
                        onClick={() => {
                          setMethodToDelete(method);
                          setDeleteDialogOpen(true);
                        }}
                        disabled={method.isDefault || processing}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>

                {/* Warning for expiring cards */}
                {method.expiryYear && method.expiryMonth && new Date(method.expiryYear, method.expiryMonth - 1) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) && (
                  <Alert
                    severity="warning"
                    icon={<WarningIcon />}
                    sx={{ mt: 2 }}
                  >
                    Card expiring soon
                  </Alert>
                )}
              </WalteroCard>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add Payment Method Modal */}
      <AddPaymentMethodModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        organizationId={organizationId}
        onSuccess={() => {
          setAddModalOpen(false);
          loadPaymentMethods();
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !processing && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Remove Payment Method?
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove this payment method?
          </Typography>
          {methodToDelete && (
            <Box mt={2} p={2} bgcolor={theme.palette.grey[100]} borderRadius={1}>
              <Typography variant="body2">
                {getCardBrandIcon(methodToDelete.brand)} ending in {methodToDelete.last4}
              </Typography>
            </Box>
          )}
          <Alert severity="info" sx={{ mt: 2 }}>
            Active subscriptions using this payment method will need to be updated.
          </Alert>
        </DialogContent>
        <DialogActions>
          <WalteroButton
            onClick={() => setDeleteDialogOpen(false)}
            disabled={processing}
          >
            CANCEL
          </WalteroButton>
          <WalteroButton
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={processing}
            startIcon={processing && <CircularProgress size={16} />}
          >
            {processing ? 'REMOVING...' : 'REMOVE'}
          </WalteroButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentMethodsTab;